import { Component, inject, OnInit, OnDestroy, signal } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { RouterOutlet } from '@angular/router'
import { AuthService } from '@auth0/auth0-angular'
import { environment } from '@environment'
import { MediaHubApiService } from '@services/media-hub-api.service'
import { PublisherHelperService } from '@services/publisher-helper.service'
import { MediaAssetHelperService } from '@services/media-asset-helper.service'
import { ProjectHelperService } from '@services/project-helper.service'
import { ChatHelperService } from '@services/chat-helper.service'
import { ToasterService } from '@services/toaster.service'
import { ThemeService } from '@services/theme.service'
import { CommonService } from '@services/common.service'
import { ToasterComponent } from '@components/toaster/toaster.component'
import { ConfirmationModalComponent } from '@components/confirmation-modal/confirmation-modal.component'
import { CoachmarkOverlayComponent } from '@components/coachmark/coachmark-overlay.component'
import { CoachmarkService } from '@services/coachmark.service'
import { clearAuthStorage } from '@utils/storage.utils'
import { forkJoin, from, firstValueFrom } from 'rxjs'
import { finalize } from 'rxjs/operators'

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ToasterComponent, ConfirmationModalComponent, CoachmarkOverlayComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit, OnDestroy {
  ts = inject(Title)
  mediaHubApi = inject(MediaHubApiService)
  auth = inject(AuthService)
  publisherHelperService = inject(PublisherHelperService)
  mediaAssetHelperService = inject(MediaAssetHelperService)
  projectHelperService = inject(ProjectHelperService)
  chatHelperService = inject(ChatHelperService)
  toasterService = inject(ToasterService)
  themeService = inject(ThemeService)
  commonService = inject(CommonService)
  coachmarkService = inject(CoachmarkService)

  isLoading = signal(false)
  currentMessage = signal<string>('')
  private messageInterval: any

  loadingMessages = [
    'Loading data...',
    'Preparing media assets...',
    'Fetching projects...',
    'Checking sources...',
    'Loading chat history...',
    'Almost ready...',
    'Running final checks...',
    'Starting system...',
    'Establishing connection...',
    'Preparing content...'
  ]

  ngOnInit(): void {
    if (environment.name !== 'prod') {
      this.ts.setTitle(`Vision by Videomagic [${environment.name}]`)
      ;(window as any).toaster = this.toasterService
      // Dev helper: `coachmarks.reset()` to replay all tours, or pass a tour id.
      ;(window as any).coachmarks = this.coachmarkService
    }

    this.auth.isAuthenticated$.subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        this.verifyTokenAndLoadData()
      } else {
        console.log('User not authenticated, skipping API checks')
      }
    })
  }

  ngOnDestroy(): void {
    if (this.messageInterval) {
      clearInterval(this.messageInterval)
    }
  }

  private showRandomMessage(): void {
    const randomIndex = Math.floor(Math.random() * this.loadingMessages.length)
    this.currentMessage.set(this.loadingMessages[randomIndex])
  }

  async apiHealthCheck(): Promise<void> {
    try {
      const res = await this.mediaHubApi.getBase('/health-check')
    } catch (e) {
      console.error(e)
    }
  }
  async apiAuthCheck(): Promise<void> {
    try {
      const res = await this.mediaHubApi.getBase('/auth-check')
    } catch (e) {
      console.error(e)
    }
  }

  private async verifyTokenAndLoadData(): Promise<void> {
    try {
      await firstValueFrom(this.auth.getAccessTokenSilently())
    } catch (error) {
      console.warn('Token refresh failed, redirecting to login...', error)
      clearAuthStorage()
      this.auth.loginWithRedirect()
      return
    }

    this.commonService.refreshDisableGeneralChat()

    this.isLoading.set(true)
    this.showRandomMessage()
    this.messageInterval = setInterval(() => {
      this.showRandomMessage()
    }, 2000)

    forkJoin({
      healthCheck: from(this.apiHealthCheck()),
      authCheck: from(this.apiAuthCheck()),
      publishers: this.publisherHelperService.loadAllPublishers(),
      assets: this.mediaAssetHelperService.loadAllAssets(),
      projects: this.projectHelperService.loadProjects(),
      chats: this.chatHelperService.getAllChats(),
    })
      .pipe(
        finalize(() => {
          this.isLoading.set(false)
          if (this.messageInterval) {
            clearInterval(this.messageInterval)
          }
        })
      )
      .subscribe({
        next: (results) => {},
        error: (error) => {},
      })
  }
}
